// v3.7.9: Full implementation — migrated from ChatSidebar.tsx
import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, ChevronRight, Layers, Check, X, FolderPlus, FolderEdit, Trash2, Plus, MessageSquare, ListTodo, Zap, Sparkles } from 'lucide-react';
import { createConversation } from '@/services/conversationService';
import { planProgress, planStatusColor } from './utils';

interface ProjectTreeProps {
  projects: any[]; plans: any[]; skills: any[];
  projectsExpanded: boolean; setProjectsExpanded: (v: boolean) => void;
  expandedProjectId: string | null; toggleProject: (pid: string) => void;
  plansExpanded: boolean; setPlansExpanded: (v: boolean) => void;
  skillsExpanded: boolean; setSkillsExpanded: (v: boolean) => void;
  onCreateProject: () => void; onRenameProject: (pid: string) => void;
  onDeleteProject: (pid: string) => void; onCreatePlan: () => void;
  onDeletePlan: (id: string) => void; onNewConversationInProject: (pid: string) => void;
  onNavigate: (tab: string) => void; onQuickSkill: ((name: string) => void) | undefined;
  lang: 'en' | 'zh'; isZh: boolean;
  creating: boolean; setCreating: (v: boolean) => void; newName: string; setNewName: (v: string) => void;
  editingProjectId: string | null; setEditingProjectId: (v: string|null) => void;
  editingName: string; setEditingName: (v: string) => void;
  projectConvs: Record<string, any[]>; loadingProjectId: string | null;
  loadingPlans: boolean; creatingPlan: boolean; setCreatingPlan: (v: boolean) => void;
  newPlanTitle: string; setNewPlanTitle: (v: string) => void;
  activeConversationId: string | null; onSelectConversation: (id: string|null) => void;
  activeTab?: string; fetchPlans?: () => void; fetchSkills?: () => void;
}

export const ProjectTree = React.memo(function ProjectTree(p: ProjectTreeProps) {
  const { projects, plans, skills, projectsExpanded, setProjectsExpanded,
    expandedProjectId, toggleProject, plansExpanded, setPlansExpanded,
    skillsExpanded, setSkillsExpanded, onCreateProject, onRenameProject,
    onDeleteProject, onCreatePlan, onDeletePlan, onNewConversationInProject,
    onNavigate, onQuickSkill, lang, isZh, creating, setCreating, newName, setNewName,
    editingProjectId, setEditingProjectId, editingName, setEditingName,
    projectConvs, loadingProjectId, loadingPlans, creatingPlan, setCreatingPlan,
    newPlanTitle, setNewPlanTitle, activeConversationId, onSelectConversation,
    activeTab, fetchPlans, fetchSkills } = p;

  return (
    <>
      {/* Project Space — collapsible block (module D enhanced) */}
      <div className="border-t border-white/[0.06]">
        <div className="flex items-center gap-2 px-4 pt-3 pb-2">
          <button
            onClick={() => setProjectsExpanded((v) => !v)}
            className="flex items-center gap-2 text-white/40 hover:text-white/70 transition-colors"
            title={isZh ? '折叠/展开项目空间' : 'Toggle project space'}
          >
            {projectsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <Layers size={12} className="text-white/30" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-white/30">
              {isZh ? '项目空间' : 'Projects'}
            </span>
          </button>
          <div className="flex-1" />
          <button
            onClick={() => onNavigate('ecosystem')} aria-label="Ecosystem"
            className="text-[10px] font-bold text-white/30 hover:text-celestial-saturn transition-colors px-1.5 py-0.5 rounded-lg hover:bg-white/[0.04]"
            title={isZh ? '查看全部项目' : 'View all projects'}
          >
            {isZh ? '全部' : 'All'}
          </button>
        </div>

        {projectsExpanded && (
          <div className="px-2 pb-2 space-y-0.5">
            {creating ? (
              <div className="flex items-center gap-1.5 px-2 py-1.5">
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onCreateProject(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
                  placeholder={isZh ? '项目名称' : 'Project name'}
                  className="flex-1 min-w-0 bg-white/[0.06] border border-white/10 rounded-lg px-2 py-1 text-xs text-white/80 placeholder:text-white/55 outline-none focus:border-celestial-saturn/40"
                />
                <button onClick={onCreateProject} className="p-1 rounded-lg text-celestial-saturn hover:bg-white/[0.06] transition-colors" title={isZh ? '创建' : 'Create'}>
                  <Check size={14} />
                </button>
                <button onClick={() => { setCreating(false); setNewName(''); }} className="p-1 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition-colors" title={isZh ? '取消' : 'Cancel'}>
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setCreating(true)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/40 hover:text-celestial-saturn hover:bg-white/[0.04] transition-all duration-200"
              >
                <FolderPlus size={14} />
                <span>{isZh ? '新建项目' : 'New Project'}</span>
              </button>
            )}

            {projects.length === 0 ? (
              <div className="px-3 py-2 text-center text-[11px] text-white/55">
                {isZh ? '还没有项目' : 'No projects yet'}
              </div>
            ) : (
              projects.map((p) => {
                const isOpen = expandedProjectId === p.id;
                const convs = projectConvs[p.id] || [];
                const isEditing = editingProjectId === p.id;
                return (
                  <div key={p.id}>
                    <div className="flex items-center gap-1 px-1 py-1 rounded-xl hover:bg-white/[0.04] transition-colors group">
                      <button
                        onClick={() => toggleProject(p.id)}
                        className="flex-1 min-w-0 flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-white/60 hover:text-white/85 transition-all duration-200"
                      >
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                        {isEditing ? (
                          <input
                            autoFocus
                            value={editingName}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') onRenameProject(p.id); if (e.key === 'Escape') setEditingProjectId(null); }}
                            onBlur={() => onRenameProject(p.id)}
                            className="flex-1 min-w-0 bg-white/[0.06] border border-white/10 rounded px-1.5 py-0.5 text-xs text-white/80 outline-none focus:border-celestial-saturn/40"
                          />
                        ) : (
                          <span className="flex-1 min-w-0 text-left truncate font-medium">{p.name}</span>
                        )}
                        {convs.length > 0 && <span className="text-[10px] text-white/55">{convs.length}</span>}
                        {isOpen ? <ChevronDown size={13} className="text-white/30" /> : <ChevronRight size={13} className="text-white/30" />}
                      </button>
                      {/* New chat in project (module D) */}
                      <button
                        onClick={(e) => { e.stopPropagation(); onNewConversationInProject(p.id); }}
                        className="p-1 rounded-lg text-white/55 opacity-0 group-hover:opacity-100 hover:text-celestial-saturn hover:bg-white/[0.06] transition-all"
                        title={isZh ? '在项目中新建对话' : 'New chat in project'}
                      >
                        <Plus size={13} />
                      </button>
                      {/* Rename */}
                      <button
                        onClick={() => { setEditingProjectId(p.id); setEditingName(p.name); }}
                        className="p-1 rounded-lg text-white/55 opacity-0 group-hover:opacity-100 hover:text-celestial-saturn hover:bg-white/[0.06] transition-all"
                        title={isZh ? '重命名项目' : 'Rename project'}
                      >
                        <FolderEdit size={13} />
                      </button>
                      {/* Delete */}
                      <button
                        onClick={() => { if (confirm(isZh ? `确认删除项目「${p.name}」？其下对话将解除归属` : `Delete project "${p.name}"?`)) onDeleteProject(p.id); }}
                        className="p-1 rounded-lg text-white/55 opacity-0 group-hover:opacity-100 hover:text-red-400 hover:bg-red-500/10 transition-all"
                        title={isZh ? '删除项目' : 'Delete project'}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-5 pr-1 pb-1">
                            {loadingProjectId === p.id ? (
                              <div className="flex items-center gap-2 px-2 py-2 text-[11px] text-white/55">
                                <div className="w-3 h-3 border-2 border-celestial-saturn/30 border-t-celestial-saturn rounded-full animate-spin" />
                                {isZh ? '加载中…' : 'Loading…'}
                              </div>
                            ) : convs.length === 0 ? (
                              <div className="px-2 py-1.5 text-[11px] text-white/55">{isZh ? '暂无对话' : 'No chats'}</div>
                            ) : (
                              convs.map((c) => (
                                <button
                                  key={c.id}
                                  onClick={() => onSelectConversation(c.id)}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-[12px] transition-all duration-200 ${
                                    activeConversationId === c.id ? 'bg-celestial-saturn/10 text-celestial-saturn' : 'text-white/50 hover:text-white/80 hover:bg-white/[0.04]'
                                  }`}
                                >
                                  <MessageSquare size={12} className="flex-shrink-0 text-white/55" />
                                  <span className="flex-1 min-w-0 truncate">{c.title || (isZh ? '未命名对话' : 'Untitled')}</span>
                                </button>
                              ))
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })
            )}

            {/* Tasks (global plans) node (module E) */}
            <div className="pt-1">
              <button
                onClick={() => { setPlansExpanded((v) => !v); if (!plansExpanded && plans.length === 0) fetchPlans?.(); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/40 hover:text-celestial-saturn hover:bg-white/[0.04] transition-all duration-200"
              >
                {plansExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                <ListTodo size={14} />
                <span>{isZh ? '任务' : 'Tasks'}</span>
                {plans.length > 0 && <span className="ml-auto text-[10px] text-white/55">{plans.length}</span>}
              </button>
              <AnimatePresence initial={false}>
                {plansExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="pl-5 pr-1 pb-1 space-y-1">
                      {creatingPlan ? (
                        <div className="flex items-center gap-1.5 px-1 py-1">
                          <input
                            autoFocus
                            value={newPlanTitle}
                            onChange={(e) => setNewPlanTitle(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') onCreatePlan(); if (e.key === 'Escape') { setCreatingPlan(false); setNewPlanTitle(''); } }}
                            placeholder={isZh ? '任务标题' : 'Task title'}
                            className="flex-1 min-w-0 bg-white/[0.06] border border-white/10 rounded-lg px-2 py-1 text-xs text-white/80 placeholder:text-white/55 outline-none focus:border-celestial-saturn/40"
                          />
                          <button onClick={onCreatePlan} className="p-1 rounded-lg text-celestial-saturn hover:bg-white/[0.06] transition-colors"><Check size={13} /></button>
                          <button onClick={() => { setCreatingPlan(false); setNewPlanTitle(''); }} className="p-1 rounded-lg text-white/30 hover:text-white/60"><X size={13} /></button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setCreatingPlan(true)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-[12px] text-white/40 hover:text-celestial-saturn hover:bg-white/[0.04] transition-all"
                        >
                          <Plus size={12} /> {isZh ? '新建任务' : 'New Task'}
                        </button>
                      )}
                      {loadingPlans ? (
                        <div className="flex items-center gap-2 px-2 py-2 text-[11px] text-white/55">
                          <div className="w-3 h-3 border-2 border-celestial-saturn/30 border-t-celestial-saturn rounded-full animate-spin" />
                          {isZh ? '加载中…' : 'Loading…'}
                        </div>
                      ) : plans.length === 0 ? (
                        <div className="px-2 py-1.5 text-[11px] text-white/55">{isZh ? '暂无任务' : 'No tasks'}</div>
                      ) : (
                        plans.map((pl) => {
                          const pct = planProgress(pl);
                          return (
                            <div key={pl.id} className="group flex items-center gap-2 px-2 py-1.5 rounded-lg text-[12px] text-white/50 hover:bg-white/[0.04]">
                              <button
                                onClick={() => onNavigate('automation')} aria-label="Automation"
                                className="flex-1 min-w-0 text-left"
                                title={isZh ? '查看自动化中心' : 'Open automation'}
                              >
                                <div className="flex items-center gap-1.5">
                                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: planStatusColor(pl.status) }} />
                                  <span className="truncate">{pl.title}</span>
                                </div>
                                <div className="mt-1 h-1 rounded-full bg-white/[0.08] overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: planStatusColor(pl.status) }} />
                                </div>
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const conv = await createConversation({ title: pl.title });
                                    onSelectConversation(conv.id);
                                  } catch (err) { console.error('[ProjectTree] Create conversation failed:', err); }
                                }}
                                className="p-1 rounded-lg text-white/55 opacity-0 group-hover:opacity-100 hover:text-celestial-saturn hover:bg-white/[0.06] transition-all"
                                title={isZh ? '新建关联对话' : 'New linked chat'}
                              >
                                <MessageSquare size={12} />
                              </button>
                              <button
                                onClick={() => onDeletePlan(pl.id)}
                                className="p-1 rounded-lg text-white/55 opacity-0 group-hover:opacity-100 hover:text-red-400 hover:bg-red-500/10 transition-all"
                                title={isZh ? '删除任务' : 'Delete'}
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Skills quick launch (module G) */}
            <div className="pt-1">
              <button
                onClick={() => { setSkillsExpanded((v) => !v); if (!skillsExpanded && skills.length === 0) fetchSkills(); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/40 hover:text-celestial-saturn hover:bg-white/[0.04] transition-all duration-200"
              >
                {skillsExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                <Zap size={14} />
                <span>{isZh ? '技能' : 'Skills'}</span>
                {skills.length > 0 && <span className="ml-auto text-[10px] text-white/55">{skills.length}</span>}
              </button>
              <AnimatePresence initial={false}>
                {skillsExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="pl-5 pr-1 pb-1">
                      {skills.length === 0 ? (
                        <div className="px-2 py-1.5 text-[11px] text-white/55">{isZh ? '暂无启用技能' : 'No active skills'}</div>
                      ) : (
                        skills.map((s) => (
                          <button
                            key={s.name}
                            onClick={() => onQuickSkill?.(s.name)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-[12px] text-white/50 hover:text-celestial-saturn hover:bg-white/[0.04] transition-all"
                            title={s.description}
                          >
                            <Sparkles size={12} className="flex-shrink-0 text-celestial-saturn/70" />
                            <span className="flex-1 min-w-0 truncate">{s.name}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>

    </>
  );
});
